import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { CheckCircle2, XCircle, RotateCcw } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useSounds } from '@/hooks/useSounds';

export default function DragDropSentence({ 
  instruction,
  words, // Karıştırılmış kelimeler
  correctOrder, // Doğru sıralama indexleri [2, 0, 1, 3] gibi
  translation, // Türkçe çeviri
  onAnswer,
  disabled = false 
}) {
  const [availableWords, setAvailableWords] = useState([...words]);
  const [selectedWords, setSelectedWords] = useState([]);
  const [isAnswered, setIsAnswered] = useState(false);
  const [isCorrect, setIsCorrect] = useState(false);
  const { playCorrect, playIncorrect, playClick } = useSounds();

  const handleWordClick = (word, fromSelected = false) => {
    if (isAnswered || disabled) return;
    playClick();

    if (fromSelected) {
      // Seçili kelimeden kaldır, mevcut kelimelere ekle
      setSelectedWords(prev => prev.filter(w => w !== word));
      setAvailableWords(prev => [...prev, word]);
    } else {
      // Mevcut kelimelerden kaldır, seçili kelimelere ekle
      setAvailableWords(prev => prev.filter(w => w !== word));
      setSelectedWords(prev => [...prev, word]);
    }
  };

  const handleReset = () => {
    if (isAnswered) return;
    playClick();
    setAvailableWords([...words]);
    setSelectedWords([]);
  };

  const handleSubmit = () => {
    if (selectedWords.length !== words.length || isAnswered) return;
    
    // Doğru cümleyi oluştur
    const correctSentence = correctOrder.map(i => words[i]).join(' ');
    const userSentence = selectedWords.join(' ');
    
    const correct = userSentence === correctSentence;
    setIsCorrect(correct);
    setIsAnswered(true);
    
    // Ses efekti çal
    if (correct) {
      playCorrect();
    } else {
      playIncorrect();
    }
    
    setTimeout(() => {
      onAnswer?.(correct, userSentence);
    }, 1500);
  };

  return (
    <div className="w-full max-w-2xl mx-auto">
      {/* Instruction */}
      <div className="mb-4">
        <p className="text-gray-500 text-sm">{instruction || 'Kelimeleri doğru sıraya dizin'}</p>
      </div>

      {/* Translation hint */}
      {translation && (
        <div className="bg-blue-50 rounded-xl p-4 mb-6 border border-blue-100">
          <p className="text-blue-700 font-medium">🇹🇷 {translation}</p>
        </div>
      )}

      {/* Selected words area */}
      <div className={cn(
        'min-h-[80px] p-4 rounded-2xl border-2 border-dashed mb-6 flex flex-wrap gap-2 items-start content-start',
        isAnswered && isCorrect ? 'border-green-500 bg-green-50' :
        isAnswered && !isCorrect ? 'border-red-500 bg-red-50' :
        selectedWords.length > 0 ? 'border-blue-500 bg-blue-50' :
        'border-gray-300 bg-gray-50'
      )}>
        {selectedWords.length === 0 ? (
          <p className="text-gray-400 w-full text-center py-4">Kelimeleri buraya sürükleyin</p>
        ) : (
          selectedWords.map((word, index) => (
            <button
              key={`selected-${index}`}
              onClick={() => handleWordClick(word, true)}
              disabled={isAnswered}
              className={cn(
                'px-4 py-2 rounded-xl font-medium text-lg transition-all',
                isAnswered && isCorrect ? 'bg-green-500 text-white' :
                isAnswered && !isCorrect ? 'bg-red-500 text-white' :
                'bg-blue-500 text-white hover:bg-blue-600 active:scale-95'
              )}
            >
              {word}
            </button>
          ))
        )}
      </div>

      {/* Available words */}
      <div className="flex flex-wrap gap-2 justify-center mb-6">
        {availableWords.map((word, index) => (
          <button
            key={`available-${index}`}
            onClick={() => handleWordClick(word, false)}
            disabled={isAnswered}
            className={cn(
              'px-4 py-2 rounded-xl font-medium text-lg transition-all',
              'bg-white border-2 border-gray-200 text-gray-700',
              'hover:border-blue-500 hover:bg-blue-50 active:scale-95',
              isAnswered && 'opacity-50'
            )}
          >
            {word}
          </button>
        ))}
      </div>

      {/* Action Buttons */}
      <div className="flex gap-3">
        {!isAnswered && (
          <>
            <Button
              onClick={handleReset}
              variant="outline"
              disabled={selectedWords.length === 0}
              className="flex-1 py-6 rounded-xl"
            >
              <RotateCcw className="w-5 h-5 mr-2" />
              Sıfırla
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={selectedWords.length !== words.length}
              className="flex-1 py-6 text-lg font-bold rounded-xl bg-green-500 hover:bg-green-600"
            >
              Kontrol Et
            </Button>
          </>
        )}
      </div>

      {/* Feedback */}
      {isAnswered && (
        <div className={cn(
          'mt-4 p-4 rounded-xl',
          isCorrect ? 'bg-green-100 border border-green-200' : 'bg-red-100 border border-red-200'
        )}>
          <div className="flex items-center gap-2 mb-2">
            {isCorrect ? (
              <>
                <CheckCircle2 className="w-6 h-6 text-green-600" />
                <span className="font-bold text-green-700">Harika! Doğru sıralama!</span>
              </>
            ) : (
              <>
                <XCircle className="w-6 h-6 text-red-600" />
                <span className="font-bold text-red-700">Yanlış sıralama</span>
              </>
            )}
          </div>
          {!isCorrect && (
            <p className="text-red-600 text-sm">
              Doğru cevap: {correctOrder.map(i => words[i]).join(' ')}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
